import { useMemo } from 'react';

const useAccountActivity = ({ jobPostings, interviewSchedules, perOrgResumes, dateRange }) => {
  return useMemo(() => {
    const start = dateRange[0] ? new Date(dateRange[0]) : new Date(new Date().setDate(new Date().getDate() - 7));
    const end = dateRange[1] ? new Date(dateRange[1]) : new Date();
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    const activityCounts = {};
    const orgAdminMap = {};

    // Process job postings
    const filteredJobPostings = jobPostings.filter((job) => {
      const postingDate = new Date(job.postingDate);
      return postingDate >= start && postingDate <= end;
    });
    filteredJobPostings.forEach((job) => {
      const orgName = job.OrganizationName || "Unknown";
      if (job.org_admin_name) {
        orgAdminMap[orgName] = job.org_admin_name;
      }
      if (!activityCounts[orgName]) {
        activityCounts[orgName] = { jobPostings: 0, interviews: 0, resumes: 0, total: 0 };
      }
      activityCounts[orgName].jobPostings += 1;
      activityCounts[orgName].total += 1;
    });

    // Process interview schedules
    const filteredInterviewSchedules = interviewSchedules
      .map((schedule) => {
        const filteredCounts = {};
        for (const [dateKey, count] of Object.entries(schedule.interview_counts || {})) {
          const interviewDate = new Date(dateKey);
          if (interviewDate >= start && interviewDate <= end) {
            filteredCounts[dateKey] = count;
          }
        }
        return { ...schedule, interview_counts: filteredCounts };
      })
      .filter((schedule) => Object.keys(schedule.interview_counts).length > 0);
    filteredInterviewSchedules.forEach((schedule) => {
      for (const [dateKey, count] of Object.entries(schedule.interview_counts)) {
        const orgName = schedule.OrganizationName || "Unknown";
        if (!activityCounts[orgName]) {
          activityCounts[orgName] = { jobPostings: 0, interviews: 0, resumes: 0, total: 0 };
        }
        activityCounts[orgName].interviews += count;
        activityCounts[orgName].total += count;
      }
    });

    // Process per-org resumes
    const filteredPerOrgResumes = perOrgResumes
      .map((org) => {
        const filteredCounts = {};
        for (const [dateKey, count] of Object.entries(org.resume_parsed_counts || {})) {
          const resumeDate = new Date(dateKey);
          if (resumeDate >= start && resumeDate <= end) {
            filteredCounts[dateKey] = count;
          }
        }
        return { ...org, resume_parsed_counts: filteredCounts };
      })
      .filter((org) => Object.keys(org.resume_parsed_counts).length > 0);
    filteredPerOrgResumes.forEach((org) => {
      for (const [dateKey, count] of Object.entries(org.resume_parsed_counts)) {
        const orgName = org.OrganizationName || "Unknown";
        if (!activityCounts[orgName]) {
          activityCounts[orgName] = { jobPostings: 0, interviews: 0, resumes: 0, total: 0 };
        }
        activityCounts[orgName].resumes += count;
        activityCounts[orgName].total += count;
      }
    });

    // Helper functions
    const getMostUsedFeature = (counts) => {
      const { jobPostings, interviews, resumes } = counts;
      const max = Math.max(jobPostings, interviews, resumes);
      if (max === 0) return "None";
      if (max === jobPostings) return "Job Postings";
      if (max === interviews) return "Interview Scheduled";
      return "Resume Parsed";
    };
    const getMostUsedCount = (counts) => Math.max(counts.jobPostings, counts.interviews, counts.resumes);

    // Build activity array
    const activityArray = Object.entries(activityCounts)
      .filter(([name]) => name !== "All Organizations" && activityCounts[name].total > 0)
      .map(([name, counts]) => ({
        name,
        ...counts,
        mostUsedFeature: getMostUsedFeature(counts),
        mostUsedCount: getMostUsedCount(counts),
        adminName: orgAdminMap[name] || "N/A",
      }));

    activityArray.sort((a, b) => b.total - a.total);

    const totalAccounts = activityArray.length;
    let mostUsed, leastUsed;
    if (totalAccounts <= 5) {
      mostUsed = activityArray.slice(0, 5);
      leastUsed = [];
    } else {
      mostUsed = activityArray.slice(0, Math.min(5, totalAccounts));
      leastUsed = activityArray.slice(Math.min(5, totalAccounts), totalAccounts);
      leastUsed.sort((a, b) => a.total - b.total);
      leastUsed = leastUsed.slice(0, Math.min(5, leastUsed.length));
    }

    return { mostUsed, leastUsed };
  }, [jobPostings, interviewSchedules, perOrgResumes, dateRange]);
};

export default useAccountActivity;